/** @format */
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    Input,
    OnChanges,
    OnInit,
    signal,
    Signal,
    ViewEncapsulation,
    WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@ngneat/transloco';
import { RuleValidationResultFor } from 'common-typescript';
import { CourseUnitCountRule, CreditsRule, EntityWithRule, RangeValidationResultState } from 'common-typescript/types';
import { LocaleService } from 'sis-common';
import { PlanStateObject } from '../../../service/plan-state.service';
import { getRuleValidationResultFromState } from '../../../service/rule-validation-result-in-state';
import { RuleClearSignalService } from '../rule-clear-signal.service';
import { RuleError, RuleErrorStateService } from '../rule-error-state.service';

/** A rule that requires a range of credits or of course units from the selections of its sub-rule */
export type RangeRule = CreditsRule | CourseUnitCountRule;

/** A range rule with its validation result, typed together so that the template can pass both to the header of the rule type */
interface RangeRuleWithResult<R extends RangeRule> {
    rule: R;
    result: RuleValidationResultFor<R>;
}

@Component({
    selector: 'sis-plan-structure-range-rule',
    templateUrl: './plan-structure-range-rule.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class PlanStructureRangeRuleComponent implements OnInit, OnChanges {
    ruleClearSignalService: RuleClearSignalService = inject(RuleClearSignalService);
    ruleErrorStateService: RuleErrorStateService = inject(RuleErrorStateService);
    localeService: LocaleService = inject(LocaleService);
    transloco: TranslocoService = inject(TranslocoService);
    destroyRef: DestroyRef = inject(DestroyRef);

    _rule: WritableSignal<RangeRule> = signal(undefined);
    @Input({ required: true }) set rule(rule: RangeRule) {
        this._rule.set(rule);
    }

    _parentModule: WritableSignal<EntityWithRule> = signal(undefined);
    @Input({ required: true }) set parentModule(parentModule: EntityWithRule) {
        this._parentModule.set(parentModule);
    }

    _planStateObject: WritableSignal<PlanStateObject> = signal(undefined);
    @Input({ required: true }) set planStateObject(planStateObject: PlanStateObject) {
        this._planStateObject.set(planStateObject);
    }

    @Input({ required: true }) ruleDepthLevel: number;
    @Input({ required: true }) headingLevel: number;
    @Input({ required: true }) groupPrefix: string;
    @Input({ required: true }) selectionUIState: 'ACTIVE' | 'DISABLED' | 'SELECTABLE' = 'ACTIVE';

    ruleLegendId: Signal<string> = computed(this.ruleLegendIdComputation());
    ruleFocusId: Signal<string> = computed(() => `${this._rule().localId}-focus`);
    /** The credits rule with its result, or undefined when the rule counts course units */
    creditsRuleWithResult: Signal<RangeRuleWithResult<CreditsRule> | undefined> = computed(() => {
        const rule = this._rule();
        return rule.type === 'CreditsRule' ? this.withResult(rule) : undefined;
    });

    /** The course unit count rule with its result, or undefined when the rule counts credits */
    courseUnitCountRuleWithResult: Signal<RangeRuleWithResult<CourseUnitCountRule> | undefined> = computed(() => {
        const rule = this._rule();
        return rule.type === 'CourseUnitCountRule' ? this.withResult(rule) : undefined;
    });

    ruleErrors: Signal<RuleError[]>;

    // Todo: Fix this workaround when the change detection is possibly fixed in Angular 18
    async ngOnChanges() {
        // https://github.com/angular/angular/issues/50320
        await 0;
        this.updateRuleErrors();
    }

    ngOnInit(): void {
        this.ruleErrors = this.ruleErrorStateService.getRuleErrors(this._rule().localId);
        this.ruleClearSignalService
            .getClearSignal()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(localId => {
                if (this._rule().localId === localId) {
                    this.ruleClearSignalService.sendClearSignal(this._rule().rule.localId);
                }
            });
    }

    updateRuleErrors(): void {
        const rangeValidationResultState = (this.creditsRuleWithResult() ?? this.courseUnitCountRuleWithResult())?.result?.result;
        const ruleErrors: RuleError[] = [];
        if (rangeValidationResultState) {
            if (rangeValidationResultState === RangeValidationResultState.LESS_REQUIRED) {
                ruleErrors.push({
                    errorId: `${this._rule().localId}-LESS_REQUIRED`,
                    errorType: 'LESS_SELECTIONS_REQUIRED',
                });
            }
        }
        const ruleDisplayName = this.resolveRuleDisplayName();
        this.ruleErrorStateService.updateRuleErrorState({
            ruleDisplayName,
            ruleFocusId: this.ruleFocusId(),
            ruleLocalId: this._rule().localId,
            errors: ruleErrors,
        });
    }

    resolveRuleDisplayName(): string {
        if (this.ruleDepthLevel === 1) {
            return this.localeService.localize(this._parentModule().name);
        }
        return `${this.transloco.translate('PLAN_EDIT.SELECTION_MODAL.RULE_BODY.SELECTION_GROUP')} ${this.groupPrefix}`;
    }

    ruleLegendIdComputation(): () => string {
        return () => `rule-legend-${this._rule().localId}`;
    }

    private withResult<R extends RangeRule>(rule: R): RangeRuleWithResult<R> {
        return { rule, result: getRuleValidationResultFromState(this._planStateObject(), this._parentModule(), rule) };
    }
}
